import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useAuth } from "@/store/AuthContext";
import { useShop } from "@/store/ShopContext";
import { supabase } from "@/lib/supabase";
import { currencySymbol, formatPrice } from "@/pages/home/components/ProductCard";
import OrderTimeline from "@/components/feature/OrderTimeline";

interface OrderItem {
  id: number;
  product_id: string;
  product_name: string;
  sku_label: string | null;
  quantity: number;
  unit_price: number | string;
  final_price: number | string | null;
  subtotal: number | string | null;
}

interface OrderRow {
  id: number;
  status: string | null;
  currency: string;
  subtotal_items: number | string | null;
  shipping_total: number | string | null;
  tax_total: number | string | null;
  discount_price: number | string | null;
  customer_notes: string | null;
  tracking_number: string | null;
  recipient: Record<string, string> | null;
  created_at: string;
  order_items: OrderItem[];
}

type StatusFilter = "all" | "pending_payment" | "paid" | "processing" | "shipped" | "delivered" | "cancelled" | "refunded";

const STATUS_TABS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "Todos" },
  { key: "pending_payment", label: "Aguardando pagamento" },
  { key: "paid", label: "Pago" },
  { key: "processing", label: "Em andamento" },
  { key: "shipped", label: "Enviado" },
  { key: "delivered", label: "Entregue" },
  { key: "cancelled", label: "Cancelado" },
];

const STATUS_META: Record<string, { label: string; icon: string; pill: string }> = {
  pending_payment: {
    label: "Aguardando pagamento",
    icon: "ri-time-line",
    pill: "bg-secondary-100 text-secondary-900",
  },
  paid: { label: "Pago", icon: "ri-check-line", pill: "bg-accent-100 text-accent-900" },
  processing: {
    label: "Em andamento",
    icon: "ri-tools-line",
    pill: "bg-accent-100 text-accent-900",
  },
  shipped: {
    label: "Enviado",
    icon: "ri-truck-line",
    pill: "bg-foreground-950 text-background-50",
  },
  delivered: {
    label: "Entregue",
    icon: "ri-checkbox-circle-line",
    pill: "bg-accent-500 text-background-50",
  },
  cancelled: {
    label: "Cancelado",
    icon: "ri-close-circle-line",
    pill: "bg-background-200 text-foreground-700",
  },
  refunded: {
    label: "Reembolsado",
    icon: "ri-refund-2-line",
    pill: "bg-background-200 text-foreground-700",
  },
};

function statusMeta(status: string | null) {
  return (
    STATUS_META[status ?? ""] ?? {
      label: status ?? "Desconhecido",
      icon: "ri-question-line",
      pill: "bg-background-200 text-foreground-700",
    }
  );
}

function formatDate(iso: string) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("pt-BR", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function AccountOrdersPage() {
  const { user, loading: authLoading, signOut } = useAuth();
  const { showToast } = useShop();
  const navigate = useNavigate();

  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (authLoading || !user) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from("order_headers")
          .select("*, order_items(*)")
          .eq("customer_id", user.id)
          .order("created_at", { ascending: false });
        if (err) throw err;
        if (alive) setOrders((data ?? []) as OrderRow[]);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Não foi possível carregar os pedidos";
        if (alive) setError(msg);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, authLoading]);

  // Redirect to login if not signed in
  if (!authLoading && !user) {
    return <Navigate to="/auth?redirect=/account/orders" replace />;
  }

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSignOut = async () => {
    await signOut();
    showToast("Você saiu da conta");
    navigate("/", { replace: true });
  };

  const initial = (user?.email ?? "L")[0]?.toUpperCase() ?? "L";
  const displayName =
    (user?.user_metadata?.full_name as string | undefined) || user?.email?.split("@")[0] || "";

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900 flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="max-w-[1120px] mx-auto w-full px-4 md:px-8 pt-6 md:pt-8 pb-4">
          <nav aria-label="Breadcrumb" className="text-[13px] text-foreground-600 mb-4">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="hover:underline cursor-pointer">Início</Link></li>
              <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
              <li><Link to="/account" className="hover:underline cursor-pointer">Minha Conta</Link></li>
              <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
              <li className="text-foreground-900">Pedidos</li>
            </ol>
          </nav>

          {/* Account header */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6 md:mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-accent-200 text-accent-900 flex items-center justify-center font-heading text-2xl">
                {initial}
              </div>
              <div>
                <p className="text-[12px] uppercase tracking-[0.18em] text-foreground-600 mb-0.5">
                  Olá, {displayName}
                </p>
                <h1 className="font-heading text-foreground-950 text-2xl md:text-3xl">
                  Meus pedidos
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/favorites"
                className="h-10 px-4 inline-flex items-center gap-2 rounded-full border border-background-300 hover:border-foreground-900 text-[13px] font-medium text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-heart-line"></i>
                Favoritos
              </Link>
              <button
                type="button"
                onClick={handleSignOut}
                className="h-10 px-4 inline-flex items-center gap-2 rounded-full border border-background-300 hover:border-foreground-900 text-[13px] font-medium text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-r-line"></i>
                Sair
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-2">
            {STATUS_TABS.map((tab) => {
              const active = filter === tab.key;
              const count =
                tab.key === "all" ? orders.length : orders.filter((o) => o.status === tab.key).length;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setFilter(tab.key)}
                  className={`h-9 px-3.5 rounded-full text-[13px] font-medium inline-flex items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? "bg-foreground-950 text-background-50"
                      : "bg-background-100 text-foreground-800 hover:bg-background-200"
                  }`}
                >
                  {tab.label}
                  {count > 0 && (
                    <span
                      className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold inline-flex items-center justify-center ${
                        active
                          ? "bg-background-50 text-foreground-950"
                          : "bg-background-50 text-foreground-700 border border-background-300"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* Body */}
        <section className="max-w-[1120px] mx-auto w-full px-4 md:px-8 pb-16">
          {loading && (
            <div className="space-y-4 animate-pulse">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-32 bg-background-100 rounded-2xl"></div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="bg-primary-100/70 border border-primary-200 rounded-2xl p-6 text-center">
              <p className="text-[14px] text-primary-700 mb-3">{error}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="h-10 px-4 inline-flex items-center gap-2 rounded-full bg-foreground-950 text-background-50 text-[13px] font-medium cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                Tentar novamente
              </button>
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-background-100 mx-auto mb-5 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-foreground-700 text-[32px]"></i>
              </div>
              <h2 className="font-heading text-foreground-950 text-2xl mb-2">
                {filter === "all" ? "Nenhum pedido ainda" : "Nada aqui neste filtro"}
              </h2>
              <p className="text-[14px] text-foreground-700 mb-6 max-w-md mx-auto">
                {filter === "all"
                  ? "Quando você comprar algo, seus pedidos aparecerão aqui."
                  : "Tente outro filtro ou volte depois do seu próximo pedido."}
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-store-2-line"></i>
                Encontre algo que você vai amar
              </Link>
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <ul className="space-y-4">
              {filtered.map((order) => {
                const meta = statusMeta(order.status);
                const sym = currencySymbol(order.currency);
                const subtotal = Number(order.subtotal_items ?? 0);
                const shipping = Number(order.shipping_total ?? 0);
                const tax = Number(order.tax_total ?? 0);
                const total = subtotal + shipping + tax - Number(order.discount_price ?? 0);
                const itemsCount = order.order_items.reduce((s, i) => s + i.quantity, 0);
                const isOpen = expanded.has(order.id);
                const recipient = order.recipient ?? {};

                return (
                  <li
                    key={order.id}
                    className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden"
                  >
                    {/* Header row */}
                    <div className="p-5 md:p-6 flex flex-wrap items-center justify-between gap-4 border-b border-background-200/60">
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.18em] text-foreground-500 mb-0.5">
                            Pedido
                          </p>
                          <p className="font-heading text-foreground-950 text-lg">#{order.id}</p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.18em] text-foreground-500 mb-0.5">
                            Data
                          </p>
                          <p className="text-[13.5px] text-foreground-900">
                            {formatDate(order.created_at)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.18em] text-foreground-500 mb-0.5">
                            Itens
                          </p>
                          <p className="text-[13.5px] text-foreground-900">
                            {itemsCount} {itemsCount === 1 ? "peça" : "peças"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.18em] text-foreground-500 mb-0.5">
                            Total
                          </p>
                          <p className="text-[14px] font-semibold text-foreground-950">
                            {sym}{formatPrice(total)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-medium ${meta.pill}`}
                        >
                          <i className={meta.icon}></i>
                          {meta.label}
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleExpand(order.id)}
                          className="h-9 px-3.5 inline-flex items-center gap-1.5 rounded-full border border-background-300 hover:border-foreground-900 text-[12.5px] font-medium text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {isOpen ? "Ocultar detalhes" : "Ver detalhes"}
                          <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                        </button>
                      </div>
                    </div>

                    {/* Item thumbnails strip */}
                    <div className="px-5 md:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {order.order_items.slice(0, 4).map((it) => (
                          <div
                            key={it.id}
                            className="flex-shrink-0 max-w-[200px] inline-flex items-center gap-2 px-2 py-1 rounded-md bg-background-100/70 border border-background-200/60"
                          >
                            <div className="w-7 h-7 rounded bg-background-200/80 flex items-center justify-center text-foreground-700">
                              <i className="ri-image-line text-[14px]"></i>
                            </div>
                            <span className="text-[12.5px] text-foreground-800 truncate">
                              {it.product_name}
                            </span>
                            <span className="text-[12px] text-foreground-600">×{it.quantity}</span>
                          </div>
                        ))}
                        {order.order_items.length > 4 && (
                          <span className="text-[12.5px] text-foreground-600">
                            +{order.order_items.length - 4} mais
                          </span>
                        )}
                      </div>
                      {order.tracking_number && (
                        <span className="text-[12.5px] text-foreground-700 inline-flex items-center gap-1.5">
                          <i className="ri-route-line"></i>
                          Rastreamento: <span className="font-medium text-foreground-950">{order.tracking_number}</span>
                        </span>
                      )}
                    </div>

                    {/* Expanded details */}
                    {isOpen && (
                      <div className="px-5 md:px-6 pb-6 pt-4 border-t border-background-200/60 bg-background-100/30">
                        {/* Timeline */}
                        <div className="mb-5">
                          <OrderTimeline
                            status={order.status}
                            createdAt={order.created_at}
                            trackingNumber={order.tracking_number}
                          />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                          {/* Items list */}
                          <div className="lg:col-span-2">
                            <p className="text-[11.5px] uppercase tracking-[0.18em] text-foreground-500 mb-3">
                              Itens
                            </p>
                            <ul className="divide-y divide-background-200/60 bg-background-50 rounded-xl border border-background-200/60">
                              {order.order_items.map((it) => {
                                const finalPrice = Number(it.final_price ?? it.unit_price ?? 0);
                                const lineSub = Number(it.subtotal ?? finalPrice * it.quantity);
                                return (
                                  <li
                                    key={it.id}
                                    className="py-3 px-4 flex items-start justify-between gap-3"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[14px] text-foreground-950 leading-snug">
                                        {it.product_name}
                                      </p>
                                      {it.sku_label && (
                                        <p className="text-[12.5px] text-foreground-600 mt-0.5">
                                          {it.sku_label}
                                        </p>
                                      )}
                                      <p className="text-[12.5px] text-foreground-600 mt-0.5">
                                        Qtd {it.quantity} · {sym}{formatPrice(finalPrice)} cada
                                      </p>
                                    </div>
                                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                                      <p className="text-[14px] font-semibold text-foreground-950 whitespace-nowrap">
                                        {sym}{formatPrice(lineSub)}
                                      </p>
                                      <Link
                                        to={`/product/${it.product_id}`}
                                        className="text-[12px] text-foreground-700 hover:text-foreground-950 underline underline-offset-4 cursor-pointer"
                                      >
                                        Comprar novamente
                                      </Link>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>

                            {order.customer_notes && (
                              <div className="mt-4 bg-background-100/70 rounded-xl p-4">
                                <p className="text-[11.5px] uppercase tracking-[0.18em] text-foreground-500 mb-1 inline-flex items-center gap-1.5">
                                  <i className="ri-quill-pen-line"></i>
                                  Sua observação
                                </p>
                                <p className="text-[13px] text-foreground-800 italic leading-6">
                                  "{order.customer_notes}"
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Side: address + totals */}
                          <div className="space-y-4">
                            <div className="bg-background-50 rounded-xl border border-background-200/60 p-4">
                              <p className="text-[11.5px] uppercase tracking-[0.18em] text-foreground-500 mb-2 inline-flex items-center gap-1.5">
                                <i className="ri-map-pin-line"></i>
                                Enviar para
                              </p>
                              <p className="text-[14px] text-foreground-950 font-medium">
                                {recipient.name ?? "—"}
                              </p>
                              <p className="text-[13px] text-foreground-700 mt-0.5 leading-6">
                                {recipient.address ?? ""}
                                {recipient.address && (recipient.city || recipient.state) && <br />}
                                {[recipient.city, recipient.state, recipient.zip].filter(Boolean).join(", ")}
                                {recipient.country && <><br />{recipient.country}</>}
                              </p>
                            </div>

                            <div className="bg-background-50 rounded-xl border border-background-200/60 p-4">
                              <p className="text-[11.5px] uppercase tracking-[0.18em] text-foreground-500 mb-3">
                                Resumo
                              </p>
                              <dl className="space-y-1 text-[13px]">
                                <div className="flex justify-between text-foreground-700">
                                  <dt>Subtotal</dt>
                                  <dd className="text-foreground-950">{sym}{formatPrice(subtotal)}</dd>
                                </div>
                                <div className="flex justify-between text-foreground-700">
                                  <dt>Frete</dt>
                                  <dd className="text-foreground-950">{sym}{formatPrice(shipping)}</dd>
                                </div>
                                <div className="flex justify-between text-foreground-700">
                                  <dt>Impostos</dt>
                                  <dd className="text-foreground-950">{sym}{formatPrice(tax)}</dd>
                                </div>
                                <div className="flex justify-between pt-2 mt-1 border-t border-background-200/60">
                                  <dt className="text-[13.5px] font-medium text-foreground-900">Total</dt>
                                  <dd className="text-[14px] font-semibold text-foreground-950">
                                    {sym}{formatPrice(total)}
                                  </dd>
                                </div>
                              </dl>
                            </div>

                            <div className="flex flex-col gap-2">
                              <Link
                                to={`/order-confirmation/${order.id}`}
                                className="h-10 inline-flex items-center justify-center gap-2 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[13px] font-medium transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-file-list-3-line"></i>
                                Abrir recibo completo
                              </Link>
                              <button
                                type="button"
                                onClick={() => showToast("Entendido! Em breve um humano de verdade entra em contato")}
                                className="h-10 inline-flex items-center justify-center gap-2 rounded-full border border-background-300 hover:border-foreground-900 text-[13px] font-medium text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-customer-service-2-line"></i>
                                Preciso de ajuda com este pedido
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}